import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// All styles
import { makeStyles } from "tss-react/mui";

// Components from MUI library
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";

// Loading related components from MUI library
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

// Common components/controls
import { setPrevState } from "../../../util/common";
import { regexName } from "../../../util/regularExpress";
import { checkReservedKeywords } from "../../../util/restrictedStatement";

const Permissions = {
  3: "None",
  1: "Read",
  2: "Read & Write",
};

const useStyles = makeStyles()((theme) => ({
  tr: {
    "& td, & th": {
      wordBreak: "break-word",
    },
    "&:nth-of-type(odd)": {
      backgroundColor: "#ffffff",

      "& td:first-child": {
        backgroundColor: "#ffffff",
      },
    },
    "&:nth-of-type(even)": {
      "& td:first-child": {
        backgroundColor: "white",
      },
    },
  },
  td: {
    fontSize: "0.85rem",
  },
  SchemaLabel: {
    color: "black",
    fontWeight: 600,
    fontSize: "0.92rem",
    "& .MuiTypography-root": {
      fontWeight: 600,
    },
  },
  iconContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    position: "absolute",
    bottom: "-21px",
    right: "15px",
    backgroundColor: "white",

    "& button": {
      border: "1px solid #c4c4c4",
      borderRadius: "2px",
    },
  },
  Select: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "2px solid #0288d1",
    },
  },
  flexCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const EditableCell = ({ parentState, setParentState, schema, accessRole, User_id, isNew = true }) => {
  const newSchemaid = schema.WorkspaceSchema_id;
  const { WorkspaceSchema_id, Name } = schema;
  const [edit, setEdit] = useState(() => isNew);
  const [value, setValue] = useState(Name);
  const [errMsg, setErrMsg] = useState("");
  const [error, setError] = useState(false);
  const { classes } = useStyles();
  const editRef = useRef();

  const schemas = parentState.fetchedSchemas;
  const newSchemas = parentState.newSchemas;

  const handleSchemaNameChange = (e) => {
    setErrMsg("");
    setError(false);
    const isValid = regexName(e.target.value, "alphaWithUnderscore");
    if (isValid) setValue(e.target.value);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setValue(Name);
    setPrevState(setParentState, {
      edit: false,
      ...(!Name
        ? {
            newSchemas: newSchemas.slice(0, -1),
            schemaPermissionDetails: parentState.schemaPermissionDetails.map((user) => {
              delete user.newSchemas[newSchemaid];
              return user;
            }),
          }
        : {}),
      error: false,
    });

    setEdit(false);
    setError(false);
    setErrMsg("");
  };

  const handleEditCellDone = (e) => {
    e.stopPropagation();
    let tempErrMsg = "";

    if (value.length === 0) tempErrMsg = "The schema name cannot be blank.";
    else if (value.length < 2) tempErrMsg = "Please enter atleast 2 character long schema name.";
    else if (checkReservedKeywords(value)) tempErrMsg = "Reserved keyword.";
    else if (
      schemas.some(
        (schema) => schema.Name.toLowerCase() === value.toLowerCase() && schema.WorkspaceSchema_id !== newSchemaid
      ) ||
      newSchemas.some(
        (schema) => schema.Name.toLowerCase() === value.toLowerCase() && schema.WorkspaceSchema_id !== newSchemaid
      )
    )
      tempErrMsg = "Schema name in use, please input a different name.";

    if (tempErrMsg !== "") {
      setError(true);
      setErrMsg(tempErrMsg);
      setPrevState(setParentState, { error: true });
      editRef.current.focus();
    } else {
      setError(false);
      setErrMsg("");
      setEdit(false);

      setPrevState(setParentState, {
        schemaPermissionDetails: parentState.schemaPermissionDetails.map((user) => {
          if (isNew) {
            user.newSchemas[newSchemaid] = {
              ...user.newSchemas[newSchemaid],
              Name: value,
            };
          } else {
            user.schemas[newSchemaid] = {
              ...user.schemas[newSchemaid],
              Name: value,
            };
          }

          return user;
        }),
        ...(isNew
          ? {
              newSchemas: parentState.newSchemas.map((schema) => {
                if (schema.WorkspaceSchema_id === newSchemaid) {
                  schema.Name = value;
                }
                return schema;
              }),
            }
          : {
              fetchedSchemas: parentState.fetchedSchemas.map((schema) => {
                if (schema.WorkspaceSchema_id === newSchemaid) {
                  schema.Name = value;
                }
                return schema;
              }),
            }),
      });
      setPrevState(setParentState, { error: false, edit: false });
    }
  };

  useEffect(() => {
    if (isNew) setPrevState(setParentState, { edit: true });
  }, [isNew]);

  const handleCheckBoxChange = (e) => {
    const { checked, value } = e.target;

    if (checked) {
      setPrevState(setParentState, {
        checkedSchemas: [value, ...parentState.checkedSchemas],
      });
    } else {
      setPrevState(setParentState, {
        checkedSchemas: parentState.checkedSchemas.filter((id) => id.toString() !== value.toString()),
      });
    }
  };

  return (
    <TableCell
      align="center"
      sx={{
        width: 180,
        maxWidth: 350,
        minWidth: 250,
        top: 0,
        cursor: "pointer",
        position: "relative",
        color: "black",
        fontSize: "0.92rem",
        fontWeight: 600,
      }}
    >
      {edit ? (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <TextField
            autoFocus
            value={value}
            onChange={handleSchemaNameChange}
            inputProps={{
              minLength: 2,
              maxLength: 64,
              style: { fontSize: "0.9rem", maxWidth: 150, display: "flex" },
            }}
            required
            size="small"
            helperText={errMsg}
            error={error}
            inputRef={editRef}
          />
          <div className={classes.iconContainer}>
            <IconButton variant="outlined" size="small" color="success" onClick={handleEditCellDone}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
            <IconButton size="small" color="error" onClick={handleCancel}>
              <ClearIcon fontSize="inherit" />
            </IconButton>
          </div>
        </div>
      ) : (
        <Box className={classes.flexCenter}>
          <FormControlLabel
            className={classes.SchemaLabel}
            key={WorkspaceSchema_id}
            value={Name}
            control={
              <Checkbox
                value={WorkspaceSchema_id}
                size="small"
                color="info"
                key={WorkspaceSchema_id}
                checked={
                  parentState.checkedSchemas.findIndex(
                    (schemaId) => schemaId.toString() === WorkspaceSchema_id.toString()
                  ) !== -1
                }
                disabled={accessRole !== 1 && schema.CreatedBy !== User_id}
              />
            }
            label={Name}
            labelPlacement="end"
            onChange={handleCheckBoxChange}
          />
          <IconButton
            disabled={accessRole !== 1 && schema.CreatedBy !== User_id}
            onClick={() => {
              setEdit(true);
              setPrevState(setParentState, { edit: true });
            }}
          >
            <EditOutlinedIcon />
          </IconButton>
        </Box>
      )}
    </TableCell>
  );
};

const SchemaTable = ({ setManageSchemaState, manageSchemaState }) => {
  const { classes } = useStyles();
  const user = useSelector((state) => state.user);
  const workspace = useSelector((state) => state.workspace);
  const { User_id } = user.currentUser;
  const accessRole = workspace.workspace.accessRole;
  const [highlightSelected, setHighlightSelected] = useState({ show: false, WorkspaceSchema_id: null });

  const handlePermissionChange = useCallback(
    (e, detail, WorkspaceSchema_id, key) => {
      //Creating user list to be updated
      const usersToBeUpdated = [];
      if (detail.checked) {
        manageSchemaState.schemaPermissionDetails.forEach(({ User_id, checked }) => {
          if (checked) usersToBeUpdated.push(User_id);
        });
      } else usersToBeUpdated.push(detail.User_id);

      const AccessType = e.target.value;

      //Updating the schema permission details for selected users
      const newSchemaPermissionDetails = [...manageSchemaState.schemaPermissionDetails];
      newSchemaPermissionDetails.forEach((user) => {
        if (usersToBeUpdated.includes(user.User_id)) {
          user[key][WorkspaceSchema_id] = {
            ...user[key][WorkspaceSchema_id],
            AccessType,
          };
        }
      });

      setPrevState(setManageSchemaState, {
        schemaPermissionDetails: newSchemaPermissionDetails,
      });
    },
    [manageSchemaState.schemaPermissionDetails]
  );

  const isAllChecked = useMemo(
    () => manageSchemaState.schemaPermissionDetails.every((user) => user.checked),
    [manageSchemaState.schemaPermissionDetails]
  );

  const isAnyChecked = useMemo(
    () => manageSchemaState.schemaPermissionDetails.some((user) => user.checked),
    [manageSchemaState.schemaPermissionDetails]
  );

  const handleSelectAllUser = useCallback((e) => {
    setManageSchemaState((prev) => ({
      ...prev,
      schemaPermissionDetails: prev.schemaPermissionDetails.map((user) => ({ ...user, checked: e.target.checked })),
    }));
  }, []);

  const handleUserSelect = useCallback((e) => {
    const { checked, value } = e.target;

    setManageSchemaState((prev) => ({
      ...prev,
      schemaPermissionDetails: prev.schemaPermissionDetails.map((user) => {
        if (parseInt(user.User_id) === parseInt(value)) user.checked = checked;
        return user;
      }),
    }));
  }, []);

  const columnsCount = useMemo(
    () => 2 + manageSchemaState.fetchedSchemas?.length + manageSchemaState.newSchemas?.length,
    [manageSchemaState.fetchedSchemas?.length, manageSchemaState.newSchemas?.length]
  );

  return (
    <Table stickyHeader sx={{ width: "100%" }} size="small">
      <TableHead sx={{ minHeight: "56px" }}>
        <TableRow>
          <TableCell
            sx={{
              minWidth: 140,
              maxWidth: 140,
              position: "sticky",
              left: 0,
              zIndex: 1000,
            }}
          ></TableCell>
          <TableCell
            sx={{
              minWidth: 140,
              maxWidth: 140,
              position: "sticky",
              left: 140,
              zIndex: 1000,
            }}
          ></TableCell>
          <TableCell
            colSpan={manageSchemaState.fetchedSchemas.length + manageSchemaState.newSchemas.length}
            align="center"
            className={classes.SchemaLabel}
          >
            SCHEMAS
          </TableCell>
        </TableRow>
        <TableRow className={classes.tr}>
          <TableCell
            style={{
              position: "sticky",
              left: 0,
              top: 37,
              zIndex: 1000,
              minWidth: 160,
              width: 160,
              paddingLeft: 0,
            }}
            className={classes.SchemaLabel}
          >
            <Checkbox
              color="info"
              checked={isAllChecked}
              indeterminate={isAnyChecked && !isAllChecked}
              onClick={handleSelectAllUser}
            />
            USER
          </TableCell>
          <TableCell
            style={{
              position: "sticky",
              left: 140,
              top: 37,
              zIndex: 1000,
              minWidth: 140,
              width: 140,
            }}
            className={classes.SchemaLabel}
          >
            WORKSPACE ROLE
          </TableCell>

          {manageSchemaState.fetchedSchemas.map((schema) => {
            return schema.Type !== 3 ? (
              <TableCell
                align="center"
                sx={{
                  width: 180,
                  maxWidth: 180,
                  minWidth: 180,
                  top: 37,
                }}
                key={schema.WorkspaceSchema_id}
              >
                <span className={classes.SchemaLabel}>{schema.Name}</span>
              </TableCell>
            ) : (
              <EditableCell
                key={schema.WorkspaceSchema_id}
                schema={schema}
                parentState={manageSchemaState}
                setParentState={setManageSchemaState}
                isNew={false}
                accessRole={accessRole}
                User_id={User_id}
              />
            );
          })}
          {manageSchemaState.newSchemas.map((schema) => (
            <EditableCell
              key={schema.WorkspaceSchema_id}
              schema={schema}
              parentState={manageSchemaState}
              setParentState={setManageSchemaState}
              isNew={true}
              accessRole={accessRole}
              User_id={User_id}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {manageSchemaState.schemaPermissionDetails.length > 0 && manageSchemaState.fetchedSchemas.length > 0 ? (
          manageSchemaState.schemaPermissionDetails.map((detail) => {
            return (
              <TableRow tabIndex={-1} key={detail.UserName}>
                <TableCell
                  style={{
                    position: "sticky",
                    left: 0,
                    minWidth: 160,
                    width: 160,
                    backgroundColor: "white",
                    zIndex: 800,
                    paddingLeft: 0,
                  }}
                  key={detail.FullName}
                  className={classes.td}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox color="info" value={detail.User_id} checked={detail.checked} onClick={handleUserSelect} />
                    {detail.FullName}
                    <span style={{ marginLeft: "auto" }}>
                      {detail.UserStatus === 0 && (
                        <Chip
                          icon={<CancelOutlinedIcon />}
                          label="Inactive"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </span>
                  </Box>
                </TableCell>
                <TableCell
                  style={{
                    position: "sticky",
                    left: 140,
                    minWidth: 140,
                    width: 140,
                    backgroundColor: "white",
                    zIndex: 800,
                  }}
                  key={detail.Role}
                  className={classes.td}
                >
                  {detail.Role}
                </TableCell>
                {manageSchemaState.fetchedSchemas.map(({ Name, WorkspaceSchema_id, Type, CreatedBy }) =>
                  Type !== 3 ? (
                    <TableCell className={classes.td} align="center" key={`${Name}_${detail.UserName}`}>
                      {Permissions[detail.schemas[WorkspaceSchema_id].AccessType]}
                    </TableCell>
                  ) : (
                    <TableCell className={classes.td} align="center" key={`${Name}_${detail.UserName}`}>
                      <FormControl
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={accessRole !== 1 && CreatedBy !== User_id}
                        {...(detail.checked &&
                        highlightSelected.show &&
                        highlightSelected.WorkspaceSchema_id === WorkspaceSchema_id
                          ? { color: "info" }
                          : {})}
                      >
                        <Select
                          onFocus={() => setHighlightSelected({ show: detail.checked, WorkspaceSchema_id })}
                          onBlur={() => setHighlightSelected({ show: false, WorkspaceSchema_id: null })}
                          required
                          value={detail.schemas[WorkspaceSchema_id]?.AccessType}
                          onChange={(e) => handlePermissionChange(e, detail, WorkspaceSchema_id, "schemas")}
                          sx={{
                            fontSize: "0.8rem",
                          }}
                          className={
                            detail.checked &&
                            highlightSelected.show &&
                            highlightSelected.WorkspaceSchema_id === WorkspaceSchema_id
                              ? classes.Select
                              : ""
                          }
                        >
                          {Object.keys(Permissions).map((key) => (
                            <MenuItem
                              key={key}
                              value={key}
                              sx={{
                                fontSize: "0.8rem",
                              }}
                            >
                              {Permissions[key]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  )
                )}
                {manageSchemaState.newSchemas.map(({ Name, WorkspaceSchema_id }) => (
                  <TableCell className={classes.td} align="center" key={`${Name}_${detail.UserName}`}>
                    <FormControl size="small" fullWidth variant="outlined">
                      <Select
                        required
                        value={detail.newSchemas[WorkspaceSchema_id].AccessType}
                        onChange={(e) => handlePermissionChange(e, detail, WorkspaceSchema_id, "newSchemas")}
                        sx={{
                          fontSize: "0.8rem",
                        }}
                      >
                        {Object.keys(Permissions).map((key) => (
                          <MenuItem
                            key={key}
                            value={key}
                            sx={{
                              fontSize: "0.8rem",
                            }}
                          >
                            {Permissions[key]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell align="center" colSpan={columnsCount}>
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default SchemaTable;
